import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const FIELD_LABEL = {
  email: 'Email address', first_name: 'First name', last_name: 'Last name',
  phone: 'Phone', city: 'City', state: 'State', country: 'Country',
};

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues]   = useState({});
  const [website, setWebsite] = useState(''); // honeypot
  const [captcha, setCaptcha] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]       = useState(null); // { message }
  const [error, setError]     = useState('');
  const captchaRef = useRef(null);

  useEffect(() => {
    axios.get(`/api/public/forms/${slug}`)
      .then(r => setForm(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.hcaptcha_enabled && !captcha) { setError('Please complete the CAPTCHA.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`/api/public/forms/${slug}/submit`, {
        ...values, website, hcaptcha_token: captcha,
      });
      if (res.data.redirect_url) { window.location.href = res.data.redirect_url; return; }
      setDone({ message: res.data.message });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      captchaRef.current?.resetCaptcha?.();
      setCaptcha('');
    } finally {
      setSubmitting(false);
    }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f7', padding: 20, fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', padding: 32 }}>
        {children}
      </div>
    </div>
  );

  if (loading)  return wrap(<div style={{ textAlign: 'center', color: '#888' }}>Loading…</div>);
  if (notFound) return wrap(<div style={{ textAlign: 'center', color: '#888' }}>This form isn’t available.</div>);

  const color = form.brand_color || '#0a40d9';

  if (done) return wrap(
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
      <h2 style={{ margin: '0 0 8px', color: '#111' }}>{done.message || 'Thank you!'}</h2>
    </div>
  );

  return wrap(
    <form onSubmit={submit}>
      <h2 style={{ margin: '0 0 6px', color: '#111', fontSize: 22 }}>{form.heading}</h2>
      {form.description && <p style={{ margin: '0 0 20px', color: '#666', lineHeight: 1.5 }}>{form.description}</p>}

      {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>{error}</div>}

      {form.fields.map(f => (
        <div key={f.key} style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 4 }}>
            {FIELD_LABEL[f.key] || f.key}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
          </label>
          <input
            type={f.key === 'email' ? 'email' : 'text'}
            required={f.required}
            value={values[f.key] || ''}
            onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
            style={{ width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
          />
        </div>
      ))}

      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        type="text" tabIndex={-1} autoComplete="off" value={website}
        onChange={e => setWebsite(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
        aria-hidden="true"
      />

      {form.hcaptcha_enabled && form.hcaptcha_sitekey && (
        <div style={{ margin: '8px 0 16px' }}>
          <HCaptcha ref={captchaRef} sitekey={form.hcaptcha_sitekey} onVerify={setCaptcha} />
        </div>
      )}

      <button
        type="submit" disabled={submitting}
        style={{ width: '100%', padding: '13px', background: color, color: '#fff', border: 0, borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
      >
        {submitting ? 'Submitting…' : (form.button_text || 'Subscribe')}
      </button>

      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#aaa' }}>
        Powered by SES Mailbox
      </div>
    </form>
  );
}
